/**
 * GameService — Supabase RPC wrappers for the game web app.
 *
 * All calls use the user_hash (Supabase auth.uid) obtained from initSession().
 * RPC functions are SECURITY DEFINER so they bypass RLS safely.
 */

import { supabase } from './supabase.js'

/**
 * Get or create the user's island record.
 * Called on game load to get current state.
 *
 * @param {string} userHash
 * @returns {Promise<{level, exp_points, total_p_spent, visual_state}|null>}
 */
export async function getOrCreateIsland(userHash) {
  const { data, error } = await supabase.rpc('get_or_create_island', {
    p_user_hash: userHash,
    p_island_type: 'time_crystal',
  })
  if (error) {
    console.error('[GameService] getOrCreateIsland error:', error.message)
    return null
  }
  return data
}

/**
 * Spend P points to upgrade the island.
 *
 * @param {string} userHash
 * @param {number} cost  P points to spend (min 10)
 * @returns {Promise<{success, new_level, new_exp, total_p_spent, new_balance, leveled_up}|null>}
 */
export async function upgradeIsland(userHash, cost = 10) {
  const { data, error } = await supabase.rpc('upgrade_island', {
    p_user_hash: userHash,
    p_island_type: 'time_crystal',
    p_p_cost: cost,
  })
  if (error) {
    console.error('[GameService] upgradeIsland error:', error.message)
    return null
  }
  return data
}

/**
 * Get current P-point balance from wallet_accounts.
 * Used to refresh the HUD balance after an upgrade.
 *
 * @param {string} userHash
 * @returns {Promise<number>}
 */
export async function getBalance(userHash) {
  const { data, error } = await supabase
    .from('wallet_accounts')
    .select('balance')
    .eq('user_hash', userHash)
    .single()

  if (error) {
    console.error('[GameService] getBalance error:', error.message)
    return 0
  }
  return data?.balance ?? 0
}
